"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  Menu, X, ArrowUpRight, ChevronDown, Layers, Workflow, Library, ShieldCheck,
  BookOpen, Sparkles, Scissors, Network, Bot, Scale, Tags, Bug, Send, Server,
  MonitorSmartphone, Container, GitCompare, ArrowLeftRight, Handshake,
  Braces, Puzzle, Laptop, KeyRound, Landmark, Stethoscope, GraduationCap,
  FlaskConical, TrendingUp, Building2,
  Newspaper, Mail, Users, Rocket, Cpu,
  Factory, Lightbulb, Radar, MessageSquareQuote, Paintbrush, Bird, Gauge,
  HeartHandshake, Check,
} from "lucide-react";
import { ThemeToggle } from "@/components/marketing/theme";
import type { LucideIcon } from "lucide-react";
import { cn, SITE } from "@/lib/utils";

/** `useLayoutEffect` warns when React renders on the server; fall back to `useEffect` there. */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;
import { APPS, APP_GROUPS, COLLECTION_COUNT } from "@/lib/apps-registry";
import { CORPORA } from "@/lib/corpora";
import { CanvasRevealEffect } from "./aceternity/canvas-reveal-effect";
import { NavLiveStats } from "./nav-live-stats";
import { INDUSTRIES } from "@/lib/taxonomy";
import { StatusBadge } from "@/components/marketing/status-badge";

/* ──────────────── Nav model ──────────────── */

type SubItem = {
  href: string;
  label: string;
  blurb?: string;
  /** Optional leading icon (e.g. an OS/platform logo), served from /shared. */
  icon?: string;
  /** Off-site link (e.g. the live status page on askfinz.ai) — opens in a new tab. */
  external?: boolean;
  /** Column heading on the desktop panel. Columns appear in order of first mention. */
  group?: string;
  /** Makes that heading a link. Set on any row in the group; the first one wins. Without this a
      section whose own page matters had to be listed twice — once as the heading and again as a
      row — which is how "Press & media" ended up appearing to be its own child. */
  groupHref?: string;
  /** Row icon on the desktop panel. `icon` above is an image path; this is a Lucide glyph. */
  lucide?: LucideIcon;
};

type PlainItem = { kind: "link"; href: string; label: string };
type DropdownItem = {
  kind: "dropdown";
  label: string;
  /** Route prefixes that should mark the trigger as "active". */
  match: string[];
  /** Width preset for the panel. */
  width: "narrow" | "wide";
  items?: SubItem[];
  /** Override renderer for the panel body — used by Workspaces / Guides / Web Index. */
  render?: "workspaces" | "guides" | "webindex";
  /** Footer link inside the panel ("All workspaces →" etc.). */
  footer?: { href: string; label: string };
  /** Promoted card in the last column of a grouped panel. */
  cta?: { href: string; icon: LucideIcon; title: string; body: string; action: string };
  /** Footer chip row, for a generated set too long to list as rows. */
  chips?: { label: string; href: string; items: { href: string; label: string }[] };
  /** Full-width strip under the grid: a destination that is not one of the columns' kind of page
      (Model Fab's Data Share: something a visitor DOES for the models, not a page about one). */
  band?: { href: string; icon: LucideIcon; kicker: string; title: string; body: string; action: string; points?: string[] };
};

type NavItem = PlainItem | DropdownItem;

const NAV: NavItem[] = [
  {
    kind: "dropdown",
    label: "Platform",
    match: ["/platform", "/agents", "/models", "/protocols", "/extension", "/desktop", "/os", "/access", "/api"],
    width: "narrow",
    /* Two groups, because eight flat entries mixed two different questions: what the platform is
       made of, and which machine you use it on. `group` drives the desktop columns; the mobile
       accordion still reads this same list, flat. */
    items: [
      { href: "/agents",    label: "Agents",            blurb: "Researcher, Coder and Navigator, each with its own remit.", group: "What it is made of", lucide: Bot },
      { href: "/models",    label: "Models",            blurb: "Many providers, and the answer names the one that wrote it.", group: "What it is made of", lucide: Sparkles },
      { href: "/protocols", label: "Protocols",         blurb: "A2A, MCP, A2P and A2UI — how the parts talk to each other.", group: "What it is made of", lucide: Network },
      { href: "/api",       label: "API",               blurb: "Build your own surface on the same platform.", group: "What it is made of", lucide: Braces },
      { href: "/extension", label: "Browser extension", blurb: "askFinz alongside whatever page you are already reading.", group: "Where it runs", lucide: Puzzle },
      { href: "/desktop",   label: "Desktop",           blurb: "A native app for Windows, macOS and Linux.", group: "Where it runs", lucide: Laptop },
      { href: "/os",        label: "askFinz OS",        blurb: "A full Linux build — kiosk and k3s node, managed from the cloud.", group: "Where it runs", lucide: Server },
      { href: "/access",    label: "Access",            blurb: "One sign-in that carries across every workspace.", group: "Where it runs", lucide: KeyRound },
    ],
    cta: {
      href: "/platform",
      icon: Layers,
      title: "How it fits together",
      body: "Agents, models and protocols are one stack — the overview walks the whole thing end to end.",
      action: "Platform overview",
    },
  },
  {
    // askFinz runs its own live index of the web, read from every surface the
    // product runs on rather than reselling a search API — this menu is where
    // that story lives.
    // NB: deliberately NOT called "Search" or "Knowledge" — both are existing
    // workspace names (/apps/search, /apps/knowledge) and would collide in the
    // same header. Likewise the Indexer OS entry is labelled "Indexer
    // appliance" so it isn't confused with askFinz OS under Platform.
    kind: "dropdown",
    label: "Web Index",
    // isActive matches exact or `prefix/`, so each sibling route is listed
    // explicitly — "/indexer" alone would not light up "/indexer_node".
    match: ["/web-index", "/how-the-index-works", "/collections", "/compare/web-index", "/compare/search-apis", "/indexer", "/indexer_node", "/indexer_os", "/indexer_docker", "/indexer_macos", "/crawler", "/submit-domain", "/web-safety", "/captcha"],
    width: "wide",
    render: "webindex",
  },
  /* Model Fab: where askFinz builds its own models. Two columns like the other menus: the fab
     itself, and the models, one row per model page; the CTA card points at the newest story.
     COPY RULES (see lib/model-fab.ts) apply here too: what a model does for people, never what it
     is built from. Adding it took the header past the room it had at 1280px, so the gap below is
     gap-6 at xl and gap-7 only from 2xl; see the breakpoint note on the <nav>. */
  {
    kind: "dropdown",
    label: "Model Fab",
    match: ["/model-fab", "/data-share"],
    width: "narrow",
    items: [
      { href: "/model-fab",           label: "Overview",   blurb: "Where askFinz makes models of its own.", group: "Model Fab", groupHref: "/model-fab", lucide: Factory },
      { href: "/model-fab#why",       label: "Why a fab",  blurb: "Work that runs all day deserves a model made for it.", group: "Model Fab", lucide: Lightbulb },
      { href: "/model-fab#together",  label: "One system", blurb: "How the four work together, step by step.", group: "Model Fab", lucide: Workflow },
      { href: "/model-fab#promises",  label: "Promises",   blurb: "What every askFinz model keeps to.", group: "Model Fab", lucide: ShieldCheck },
      { href: "/model-fab/sonar",     label: "Sonar",      blurb: "Keeps the web index clean, every page in its place.", group: "Models", groupHref: "/model-fab#models", lucide: Radar },
      { href: "/model-fab/qorra",     label: "Qorra",      blurb: "askFinz's own assistant. Answers with sources.", group: "Models", lucide: MessageSquareQuote },
      { href: "/model-fab/tirra",     label: "Tirra",      blurb: "Keeps all of askFinz's AI in step.", group: "Models", lucide: Paintbrush },
      { href: "/model-fab/unira",     label: "Unira",      blurb: "Many copies of Qorra, one big job.", group: "Models", lucide: Bird },
      { href: "/model-fab/bench",     label: "Benchmarks", blurb: "A live leaderboard, and where Qorra stands.", group: "Models", lucide: Gauge },
    ],
    cta: {
      href: "/model-fab/qorra#how-it-works",
      icon: MessageSquareQuote,
      title: "Meet Qorra",
      body: "Follow one question from the moment you ask to an answer whose every part you can check, and one task to the moment it asks for your OK.",
      action: "See how it works",
    },
    band: {
      href: "/data-share",
      icon: HeartHandshake,
      kicker: "Data Share",
      title: "Help improve askFinz and its models",
      body: "Share a chat from another AI assistant. We check it and remove anything private first.",
      action: "Share a conversation",
      points: ["Share links or exports", "Checked for prompt injection", "Private details removed"],
    },
  },
  {
    kind: "dropdown",
    label: "Workspaces",
    match: ["/apps"],
    width: "wide",
    render: "workspaces",
  },
  {
    kind: "dropdown",
    label: "Solutions",
    match: ["/solutions", "/for"],
    width: "narrow",
    /* The four /for rows are new to this menu. They were reachable only from the /for hub, which
       is a lot of pages to hang off one link — and they answer the question a reader actually
       arrives with ("can it do MY job?") better than an industry name does. */
    items: [
      { href: "/solutions/wealth",     label: "Wealth",     blurb: "Portfolios, planning and advisory work.", group: "By industry", lucide: Landmark },
      { href: "/solutions/healthcare", label: "Healthcare", blurb: "Records, references and intake.", group: "By industry", lucide: Stethoscope },
      { href: "/solutions/education",  label: "Education",  blurb: "Tutors, curricula and assessment.", group: "By industry", lucide: GraduationCap },
      { href: "/solutions/research",   label: "Research",   blurb: "Studies that cite what they were built on.", group: "By industry", lucide: FlaskConical },
      { href: "/solutions/operations", label: "Operations", blurb: "Inbox, workflows and the routine work around them.", group: "By industry", lucide: Workflow },
      { href: "/for/equity-research",      label: "Equity research",      blurb: "Filings, transcripts and coverage in one place.", group: "By the job", lucide: TrendingUp },
      { href: "/for/due-diligence",        label: "Due diligence",        blurb: "Check a company against sources you can open.", group: "By the job", lucide: ShieldCheck },
      { href: "/for/competitive-analysis", label: "Competitive analysis", blurb: "Track what rivals ship, price and say.", group: "By the job", lucide: GitCompare },
      { href: "/for/literature-review",    label: "Literature review",    blurb: "Papers read and summarised with citations intact.", group: "By the job", lucide: BookOpen },
    ],
    cta: {
      href: "/solutions",
      icon: Building2,
      title: "Built on one index",
      body: "Every solution reads the same libraries askFinz indexes itself — not a search API bought per query.",
      action: "All solutions",
    },
    chips: {
      label: "More use cases",
      href: "/for",
      items: [
        { href: "/for/market-research", label: "Market research" },
        { href: "/for/legal-research", label: "Legal research" },
        { href: "/for/clinical-research", label: "Clinical research" },
        { href: "/for/patent-search", label: "Patent search" },
        { href: "/for/consultants", label: "Consultants" },
        { href: "/for/developers", label: "Developers" },
      ],
    },
  },
  {
    kind: "dropdown",
    label: "Guides",
    match: ["/guides", "/integrations", "/research"],
    width: "wide",
    render: "guides",
    footer: { href: "/guides", label: "All guides" },
  },
  { kind: "link", href: "/pricing",  label: "Pricing" },
  {
    kind: "dropdown",
    label: "Company",
    match: ["/about", "/infrastructure", "/contact", "/careers", "/press", "/scale", "/colab"],
    width: "narrow",
    /* TWO GROUPS, and the split is the point. "Who we are" is the reader checking whether we are
       real; "Press & media" is a journalist or an investor looking for something citable. They
       arrive with different questions and the second lot never want the careers page. */
    items: [
      { href: "/about",          label: "About",          blurb: "Who builds askFinz, and what it is for.", group: "Who we are", lucide: Building2 },
      { href: "/infrastructure", label: "Infrastructure", blurb: "The machines behind the index, and where they sit.", group: "Who we are", lucide: Server },
      { href: "/contact",        label: "Contact",        blurb: "Reach a person, not a ticket queue.", group: "Who we are", lucide: Mail },
      { href: "/careers",        label: "Careers",        blurb: "One builder today, unusually wide surface area.", group: "Who we are", lucide: Users },
      /* The press rows are injected from the live release list — see `pressData` in layout.tsx and
         the merge below. Nothing is listed here, because a hardcoded slug is a link that rots the
         day a release is renamed. */
    ],
    cta: {
      href: "/scale",
      icon: Rocket,
      title: "Help us scale",
      body: "For companies, investors, institutions, accelerators and funding programmes — four ways in, and the arithmetic behind them.",
      action: "Scale with askFinz",
    },
  },
];

/* Collaborations: a third Company column, after Press so the two existing columns keep their
   places. Appended in the merge below rather than in NAV's items because the press rows are
   injected there and group order is order of first appearance. */
const COLLAB_ROWS: SubItem[] = [
  { href: "/colab/easi",     label: "EAISI",    blurb: "TU/e's Eindhoven AI Systems Institute, in Neuron.", group: "Collaborations", lucide: GraduationCap },
  { href: "/colab/snellius", label: "Snellius", blurb: "The Dutch national supercomputer, run by SURF.", group: "Collaborations", lucide: Cpu },
  { href: "/colab/tue-hpc",  label: "TU/e HPC", blurb: "Supercomputing at Eindhoven University of Technology.", group: "Collaborations", lucide: Server },
  { href: "/colab/kto",      label: "TU/e KTO", blurb: "TU/e's Knowledge Transfer Office, for ventures.", group: "Collaborations", lucide: Lightbulb },
];

/* ──────────────── Data passed from the (server) layout ──────────────── */

export type NavLink = { href: string; label: string; blurb?: string };
export type GuidesNavData = { guides: NavLink[]; research: NavLink[] };



/* ──────────────── Active matcher ──────────────── */

function isActive(pathname: string, prefixes: string[]) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/* ──────────────── Desktop dropdown ──────────────── */

function DesktopDropdown({
  item,
  pathname,
  guidesData,
}: {
  item: DropdownItem;
  pathname: string;
  guidesData?: GuidesNavData;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const active = isActive(pathname, item.match);

  const clearClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearClose();
    /* 260ms, not 150. The bridge above removes the dead gap, but a reader travelling diagonally
       from one trigger towards a row on the far side of a 1,100px panel still clips the corner of
       the group on the way out. The old delay was short enough to lose them. */
    closeTimer.current = setTimeout(() => setOpen(false), 260);
  }, [clearClose]);

  useEffect(() => () => clearClose(), [clearClose]);

  /* NAVIGATING OUT OF A FLYOUT HAS TO SHUT IT, AND CLOSING `open` DOES NOT DO THAT.
     The panel is revealed by three things — `.nav-group:hover`, `:focus-within`, and
     `data-open` — and a click on one of its links moves neither the pointer nor, on some routes,
     the focus. So the panel sat over the page the user had just asked for. `dismissed` is the
     fourth state: it outranks all three in CSS and lasts until the pointer leaves the group. */
  const [dismissed, setDismissed] = useState(false);

  const dismiss = useCallback(() => {
    setOpen(false);
    setDismissed(true);
    /* Focus left inside a hidden panel would hold `:focus-within` open the moment `dismissed`
       clears. Only blur when focus is actually in there — never steal it from anywhere else. */
    const focused = document.activeElement as HTMLElement | null;
    if (focused && panelRef.current?.contains(focused)) focused.blur();
  }, []);

  /* Belt and braces for a navigation that did not come from a click in the panel — a keyboard
     activation that the click handler misses, or a programmatic push. Skips the first run: on
     mount nothing has been navigated away from, and dismissing here would leave the menu shut
     until the pointer had entered and left once. */
  const seenPath = useRef(pathname);
  useEffect(() => {
    if (seenPath.current === pathname) return;
    seenPath.current = pathname;
    dismiss();
  }, [pathname, dismiss]);

  // Close on Escape, regardless of focus location.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  /* Position the panel with `left`, NOT `transform`.
   *
   * THE BUG THIS FIXES, and it is not obvious. The panel carries
   * `animate-[blur-fade-in_180ms_ease-out]`, and that shared keyframe sets
   * `transform: translateY(...)`. A running CSS animation beats an inline style in the cascade,
   * and `transform` is one property — so the animation's translateY REPLACED the horizontal
   * translation entirely. Measured live at a 957px viewport: computed transform came back as
   * `matrix(1,0,0,1,0,12)` — X exactly 0 — while the inline style still read
   * `translateX(calc(-50% - 192px))`. Neither the -50% centring nor the correction ever applied,
   * so the panel sat wherever `left:50%` put it and hung 176px off the right edge.
   *
   * Setting `left` sidesteps the collision completely: the animation keeps `transform` for its
   * vertical fade, and horizontal position lives on a property nothing else touches.
   *
   * useLayoutEffect, so the position is correct before the browser paints — the panel only exists
   * while open, so there is no server-render to disagree with. */
  useIsomorphicLayoutEffect(() => {
    if (!open) return;

    const GUTTER = 16;
    const place = () => {
      const el = panelRef.current;
      if (!el) return;
      const parent = el.offsetParent as HTMLElement | null;
      const trigger = el.parentElement?.querySelector("button");
      if (!parent || !trigger) return;

      const vw = document.documentElement.clientWidth;
      // Cap to the REAL usable width. `100vw` in CSS would include the scrollbar gutter.
      const maxW = Math.max(280, vw - GUTTER * 2);
      el.style.maxWidth = `${maxW}px`;

      const w = Math.min(el.offsetWidth, maxW);
      const t = trigger.getBoundingClientRect();
      const pr = parent.getBoundingClientRect();

      // Centre on the trigger, then clamp both edges into the viewport.
      let viewportLeft = t.left + t.width / 2 - w / 2;
      viewportLeft = Math.min(viewportLeft, vw - GUTTER - w);
      viewportLeft = Math.max(viewportLeft, GUTTER);

      el.style.left = `${Math.round(viewportLeft - pr.left)}px`;

      /* Vertical cap, from the panel's real top rather than an assumed header height. The CSS
         `max-h` guesses 5.5rem of chrome, which is wrong while the promo banner is up — that left
         Workspaces hanging 13px below the fold at 1280x720. Guarded write: this runs inside a
         ResizeObserver on `el` itself, so setting the height unconditionally would re-trigger it. */
      const avail = Math.max(240, Math.round(window.innerHeight - el.getBoundingClientRect().top - GUTTER));
      const next = `${avail}px`;
      if (el.style.maxHeight !== next) el.style.maxHeight = next;
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });
    const ro = new ResizeObserver(place);
    ro.observe(panelRef.current!);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
      ro.disconnect();
    };
  }, [open]);

  const onTriggerKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      // Focus first link inside the panel after it renders.
      requestAnimationFrame(() => {
        const first = panelRef.current?.querySelector<HTMLAnchorElement>("a[href]");
        first?.focus();
      });
    }
  };

  return (
    <div
      /* `self-stretch` is what keeps the panel out of the header. The panel is `top-full` on this
         wrapper, so the wrapper must be the full height of the bar — otherwise it is only as tall
         as the button text, centred, and the panel opens ~42px down INSIDE a 64px header. */
      className="nav-group relative flex items-center self-stretch"
      data-dismissed={dismissed ? "true" : undefined}
      onMouseEnter={() => {
        clearClose();
        setOpen(true);
      }}
      /* Leaving the group is what re-arms the menu. Clearing on ENTER instead would not work:
         the pointer is already inside when a link is taken, so no enter fires and the panel
         would spring back open over the new page. */
      onMouseLeave={() => {
        setDismissed(false);
        scheduleClose();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onKeyDown={onTriggerKey}
        onFocus={() => clearClose()}
        className={cn(
          "nav-link inline-flex items-center gap-1 whitespace-nowrap text-foreground/70 hover:text-foreground",
          active && "text-foreground"
        )}
      >
        <span className="relative">
          {item.label}
          {active && (
            <span
              aria-hidden
              className="absolute -right-2 -top-1 h-1 w-1 rounded-full bg-[hsl(var(--accent))]"
            />
          )}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {/* ALWAYS RENDERED, revealed by CSS. This used to be `{open && ...}`, so the panel did not
          exist in the document until React had hydrated — measured at 917ms on the live home page,
          against a header that is painted at ~300ms. For that second the menus were on screen and
          dead, which is why a click "did not register". `nav-flyout` hides it and `.nav-group:hover`
          shows it, so hovering works from first paint with no JavaScript; React still drives the
          close delay, the keyboard and the arrow. */}
      <div
          ref={panelRef}
          id={panelId}
          role="menu"
          data-open={open ? "true" : "false"}
          /* Dismiss on the CLICK rather than waiting for the route to change, for two reasons:
             it shuts immediately instead of after the navigation resolves, and it also covers a
             link to the page you are already on — where the pathname never changes and the
             effect above would never fire. Delegated, because these panels build their links in
             four different places. */
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a[href]")) dismiss();
          }}
          onMouseEnter={clearClose}
          onMouseLeave={scheduleClose}
          className={cn(
            "nav-flyout",
            "absolute top-full mt-1 z-50",
            "border hairline-strong bg-card/95 backdrop-blur-md rounded-xl shadow-lg",
            /* A panel taller than the screen is a panel whose bottom rows do not exist. Workspaces
               is 788px; on a 768px-high laptop that loses its last four verticals with no way to
               reach them. `dvh` rather than `vh` because on mobile Chrome `vh` is the LARGEST
               viewport, so a vh cap still overflows while the address bar is showing. */
            "max-h-[calc(100dvh-5.5rem)] overflow-y-auto overscroll-contain",
            /* The entrance animation is gone: the panel is always in the DOM now, so a keyframe
               animation would run at page load and flash every menu open before settling. The
               reveal is the .nav-flyout opacity transition in globals.css. */
            /* max-w is the floor of the protection: even before the measurement runs, a panel can
               never be wider than the screen. The `wide` panels had none, so anything under ~950px
               overflowed no matter where the trigger sat. */
            "max-w-[calc(100vw-2rem)]",
            /* Width follows the column count, not a "narrow/wide" guess: five columns for the two
               mega-panels, four for Workspaces, three for a grouped panel with a CTA. */
            item.render === "webindex" || item.render === "guides"
              ? "w-[min(1180px,calc(100vw-2rem))]"
              : item.render === "workspaces"
              ? "w-[min(1100px,calc(100vw-2rem))]"
              : new Set((item.items ?? []).map((r) => r.group ?? "")).size > 2
              ? "w-[min(1040px,calc(100vw-2rem))]"
              : "w-[min(780px,calc(100vw-2rem))]"
          )}
        >
          {item.render === "workspaces" ? (
            <WorkspacesPanel pathname={pathname} />
          ) : item.render === "webindex" ? (
            <WebIndexPanel pathname={pathname} />
          ) : item.render === "guides" ? (
            <GuidesPanel pathname={pathname} data={guidesData} />
          ) : (
            <GroupedPanel item={item} pathname={pathname} />
          )}

          {item.footer && (
            <div className="border-t hairline px-4 py-2.5">
              <Link
                role="menuitem"
                href={item.footer.href}
                className="inline-flex items-center gap-1 text-xs text-foreground/70 hover:text-foreground"
              >
                {item.footer.label}
                <ArrowUpRight className="h-3 w-3" aria-hidden />
              </Link>
            </div>
          )}
        </div>
    </div>
  );
}

/* ──────────────── Workspaces panel (3-col grid by group) ──────────────── */

function WorkspacesPanel({ pathname }: { pathname: string }) {
  const [ctaHover, setCtaHover] = useState(false);
  const live = APPS.filter((a) => a.status !== "soon").length;
  const soon = APPS.length - live;

  return (
    <div className="p-2">
      <div className="grid grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,0.8fr)] divide-x divide-[hsl(var(--foreground)/0.06)] rounded-xl border hairline bg-card">
        {APP_GROUPS.map((group) => {
          const apps = APPS.filter((a) => a.category === group.id);
          return (
            <div key={group.id} className="min-w-0 p-2">
              <div className="kicker px-3 pt-1.5">{group.label}</div>
              <div className="mt-1.5 flex flex-col gap-0.5">
                {apps.map((app) => {
                  const href = `/apps/${app.slug}`;
                  const isOn = pathname === href;
                  /* Two-letter monogram from the registry — the same mark /apps/<slug> uses. */
                  const glyph = (app.accent ?? app.name).slice(0, 2);
                  return (
                    <Link
                      key={app.slug}
                      role="menuitem"
                      href={href}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-1.5",
                        "transition-colors duration-200 hover:border-border hover:bg-foreground/[0.03]",
                        "focus-visible:border-border focus-visible:bg-foreground/[0.03] focus:outline-none",
                        isOn && "border-border bg-foreground/[0.04]"
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "flex size-7 shrink-0 items-center justify-center rounded-md border hairline",
                          "font-mono text-[10.5px] leading-none tracking-tight text-foreground/55",
                          "transition-colors duration-200 group-hover:bg-foreground group-hover:text-background",
                          isOn && "bg-foreground text-background"
                        )}
                      >
                        {glyph.charAt(0).toUpperCase()}
                        <span className="opacity-75">{glyph.charAt(1).toLowerCase()}</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-[13px] font-medium leading-tight text-foreground">
                            {app.name}
                          </span>
                          {/* Only roadmap workspaces are badged. Badging all 29 makes a wall of
                              pills and buries the one distinction a reader needs. Pushed to the
                              trailing edge so the name is never the thing that gets squeezed. */}
                          {app.status === "soon" && (
                            <StatusBadge status={app.status} size="compact" className="ml-auto shrink-0" />
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] leading-snug text-foreground/55">
                          {app.tagline}
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="min-w-0 p-2">
          <PanelCta
            href="/apps"
            icon={Layers}
            title="One login, every room"
            action={`Compare all ${APPS.length}`}
            hover={ctaHover}
            setHover={setCtaHover}
          >
            {/* Counted off the registry rather than typed, so the sentence cannot go stale the
                way a written-down total does. */}
            {live} workspaces are live today and {soon} are on the roadmap — all sharing one
            sign-in, one memory and one model layer.
          </PanelCta>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5">
        <span className="text-[12px] text-foreground/55">
          Every workspace reads from the same index askFinz builds itself.
        </span>
        <div className="flex items-center gap-1">
          {[
            { href: "/platform", label: "The platform" },
            { href: "/pricing", label: "Pricing" },
            { href: "/web-index", label: "The index" },
          ].map((l) => (
            <Link
              key={l.href}
              role="menuitem"
              href={l.href}
              className="px-2.5 py-1 text-[12px] text-foreground/55 transition-colors hover:text-foreground"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Grouped panel — Platform, Solutions ────────────────
   Columns are derived from `items` by their `group`, so this menu and the mobile accordion read
   ONE list. Writing a second `panel:` structure beside `items:` would have been fewer lines and
   would have drifted the first time someone added a link to only one of them. ── */

function GroupedPanel({ item, pathname }: { item: DropdownItem; pathname: string }) {
  const [ctaHover, setCtaHover] = useState(false);
  const rows = item.items ?? [];

  /* Order of first appearance — the NAV array is the running order, not an alphabetical one. */
  const groups: { title: string; href?: string; rows: SubItem[] }[] = [];
  for (const r of rows) {
    const title = r.group ?? "";
    const existing = groups.find((g) => g.title === title);
    if (existing) {
      existing.rows.push(r);
      if (!existing.href && r.groupHref) existing.href = r.groupHref;
    } else {
      groups.push({ title, href: r.groupHref, rows: [r] });
    }
  }

  return (
    <div className="p-2">
      <div
        className="grid divide-x divide-[hsl(var(--foreground)/0.06)] rounded-xl border hairline bg-card"
        /* Columns follow the group count (Company has three since the Collaborations column). A
           static class can not, and three groups in a two-column grid wrapped the third under the first. */
        style={{
          gridTemplateColumns: item.cta
            ? `repeat(${groups.length},minmax(0,1fr)) minmax(0,0.82fr)`
            : `repeat(${groups.length},minmax(0,1fr))`,
        }}
      >
        {groups.map((g) => (
          <div key={g.title} className="min-w-0 p-2">
            {g.title &&
              (g.href ? (
                <Link
                  href={g.href}
                  role="menuitem"
                  className="kicker block px-3 pt-1.5 pb-2 transition-colors hover:text-foreground"
                >
                  {g.title}
                </Link>
              ) : (
                <div className="kicker px-3 pt-1.5 pb-2">{g.title}</div>
              ))}
            <div className="flex flex-col gap-0.5">
              {g.rows.map((r) => (
                <PanelRowLink
                  key={r.href}
                  pathname={pathname}
                  r={{
                    href: r.href,
                    label: r.label,
                    desc: r.blurb ?? "",
                    icon: r.lucide ?? ArrowUpRight,
                    external: r.external,
                    img: r.icon,
                  }}
                />
              ))}
            </div>
          </div>
        ))}

        {item.cta && (
          <div className="min-w-0 p-2">
            <PanelCta
              href={item.cta.href}
              icon={item.cta.icon}
              title={item.cta.title}
              action={item.cta.action}
              hover={ctaHover}
              setHover={setCtaHover}
            >
              {item.cta.body}
            </PanelCta>
          </div>
        )}
      </div>

      {item.band && (
        <Link
          href={item.band.href}
          role="menuitem"
          className="group mt-2 flex items-center gap-4 rounded-xl border hairline bg-[hsl(var(--accent)/0.06)] px-4 py-3.5 transition-colors duration-200 hover:border-[hsl(var(--accent)/0.4)] hover:bg-[hsl(var(--accent)/0.1)]"
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--accent)/0.35)] text-[hsl(var(--accent))] transition-colors duration-200 group-hover:bg-[hsl(var(--accent))] group-hover:text-background">
            <item.band.icon className="h-4 w-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--accent))]">{item.band.kicker}</span>
              <span className="text-[13px] font-medium leading-tight text-foreground">{item.band.title}</span>
            </span>
            <span className="mt-1 block text-[11.5px] leading-snug text-foreground/60">{item.band.body}</span>
          </span>
          {item.band.points && (
            <span className="hidden shrink-0 flex-col gap-1 border-l hairline pl-4 xl:flex">
              {item.band.points.map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5 text-[11px] text-foreground/60">
                  <Check className="h-3 w-3 text-[hsl(var(--accent))]" aria-hidden />
                  {p}
                </span>
              ))}
            </span>
          )}
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--accent))]">
            {item.band.action}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden />
          </span>
        </Link>
      )}

      {item.chips && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2.5">
          <Link
            role="menuitem"
            href={item.chips.href}
            className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45 transition-colors hover:text-foreground"
          >
            {item.chips.label}
          </Link>
          {item.chips.items.map((c) => (
            <PanelChip key={c.href} href={c.href}>
              {c.label}
            </PanelChip>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────── Guides panel (Guides · Integrations · Research) ──────────────── */


/* ── Web Index mega-panel ──────────────────────────────────────────────────────────────────
   Four divided columns of icon rows, each row saying what the page is FOR, plus a footer strip
   for the two generated sets. Replaces a flat list of bare labels that told a reader nothing.

   The generated sets matter here: /collections/<slug> and /web-index/for/<industry> are produced
   from lib/corpora.ts and lib/taxonomy.ts, so the menu grows on its own as those registries do.
   They are shown as chips rather than rows because there are 18 and 12 of them — a menu that
   listed every one would be a directory. The hub links carry the full sets. ── */
type PanelRow = { href: string; label: string; desc: string; icon: LucideIcon; external?: boolean; img?: string };

/* One definition of a menu column, rendered by the desktop panels AND the mobile accordion.
   Before this there were two lists per menu — the desktop rows and a flat `rows` array inside
   MobileAccordion that restated the same hrefs with no descriptions. Two lists is how the phone
   menu ended up a generation behind the desktop one. */
type PanelColumn = { title: string; href: string; rows: PanelRow[]; more?: { href: string; label: string } };

const WEB_INDEX_COLUMNS: PanelColumn[] = [
  {
    title: "The index",
    href: "/web-index",
    rows: [
      { href: "/web-index", label: "Overview", desc: "What askFinz reads, and how it files it.", icon: Layers },
      { href: "/web-index/best-web-index-for-ai", label: "Best index for AI", desc: "Why this one, against renting someone else's results.", icon: Sparkles },
      { href: "/how-the-index-works", label: "How it works", desc: "Read, filed, kept current — stage by stage.", icon: Workflow },
      /* Derived: what the reader finds on the page this row opens. COLLECTION_COUNT is the frozen
         floor for `metadata` exports that cannot await; a component can just read the registry. */
      { href: "/collections", label: "Collections", desc: `${CORPORA.length} libraries, filed by document type.`, icon: Library },
      { href: "/web-safety", label: "Web safety", desc: "What we keep out, without over-blocking.", icon: ShieldCheck },
    ],
  },
  {
    title: "Understand indexing",
    href: "/web-index/what-is-web-indexing",
    rows: [
      { href: "/web-index/what-is-web-indexing", label: "What is web indexing?", desc: "Crawl, read, store, retrieve — in plain English.", icon: BookOpen },
      { href: "/web-index/ai-web-indexer", label: "The AI web indexer", desc: "Reads a page for meaning, not for its links.", icon: Sparkles },
      { href: "/web-index/vs-web-scraping", label: "Indexing vs scraping", desc: "Read once and reuse, not a fee per page.", icon: Scissors },
      { href: "/web-index/no-proxies", label: "No rented proxies", desc: "Our own machines — nothing to rotate.", icon: Network },
      { href: "/web-index/for-ai-agents", label: "An index for agents", desc: "One index behind Chat, Research, Search and News.", icon: Bot },
    ],
  },
  {
    title: "Compare",
    href: "/compare/web-index",
    rows: [
      { href: "/compare/web-index", label: "Own index vs rented", desc: "Why owning it sets your price and your ceiling.", icon: Scale },
      { href: "/compare/search-apis", label: "Web search pricing", desc: "Per-1,000 rates across the market, each verified.", icon: Tags },
      { href: "/compare/askfinz-vs-exa", label: "vs Exa", desc: "Three of their tiers, next to ours.", icon: GitCompare },
      { href: "/compare/bing-search-api-alternative", label: "Bing API alternative", desc: "Retired in 2025 — where everything moved.", icon: ArrowLeftRight },
    ],
    more: { href: "/compare", label: "All comparisons" },
  },
  {
    title: "Run a node",
    href: "/crawler",
    rows: [
      { href: "/indexer_os", label: "Indexer OS", desc: "A Linux build for a spare machine.", icon: Server, img: "/shared/logos/platforms/ubuntu.png" },
      { href: "/indexer_node", label: "For Windows", desc: "One PowerShell line, no reformat.", icon: MonitorSmartphone, img: "/shared/logos/platforms/windows.png" },
      { href: "/indexer_macos", label: "For macOS (M1+)", desc: "One line on an Apple Silicon Mac.", icon: Laptop, img: "/shared/logos/platforms/apple.png" },
      { href: "/indexer_docker", label: "For Docker", desc: "One command on any Docker host.", icon: Container, img: "/shared/logos/platforms/docker.png" },
      { href: "/crawler", label: "The crawler", desc: "askFinz-Crawler, documented for site owners.", icon: Bug },
    ],
  },
];

const GUIDES_COLUMNS: PanelColumn[] = [
  {
    title: "Start here",
    href: "/guides",
    rows: [
      { href: "/guides/what-is-an-ai-workspace", label: "What is an AI workspace?", desc: "Why a room per discipline beats one chat box.", icon: BookOpen },
      { href: "/guides/what-is-a-multi-model-ai-platform", label: "Multi-model, explained", desc: "Routing a question to the model that suits it.", icon: Sparkles },
      { href: "/guides/what-is-retrieval-augmented-generation-rag", label: "What is RAG?", desc: "How an answer gets grounded in real sources.", icon: Layers },
      { href: "/guides/how-ai-model-routing-works", label: "How routing works", desc: "Who picks the model, and how you override it.", icon: Workflow },
    ],
    more: { href: "/guides", label: "All guides" },
  },
  {
    title: "Connect your tools",
    href: "/integrations",
    rows: [
      { href: "/integrations/google-drive", label: "Google Drive", desc: "Docs, sheets and slides, searchable in one place.", icon: Library },
      { href: "/integrations/notion", label: "Notion", desc: "Wikis and databases read alongside everything else.", icon: BookOpen },
      { href: "/integrations/slack", label: "Slack", desc: "Decisions buried in threads, findable again.", icon: Bot },
      { href: "/integrations/github", label: "GitHub", desc: "Repos, pull requests and docs in the same search.", icon: Server },
    ],
    more: { href: "/integrations", label: "All integrations" },
  },
  {
    title: "Compare",
    href: "/compare",
    rows: [
      { href: "/compare/askfinz-vs-chatgpt", label: "vs ChatGPT", desc: "Where a workspace beats a single chat surface.", icon: GitCompare },
      { href: "/compare/askfinz-vs-perplexity", label: "vs Perplexity", desc: "An owned index against an answer engine.", icon: GitCompare },
      { href: "/compare/askfinz-vs-claude", label: "vs Claude", desc: "One strong model, or the right one per task.", icon: GitCompare },
      { href: "/compare/web-index", label: "Own index vs rented", desc: "What owning the index changes about the price.", icon: Scale },
    ],
    more: { href: "/compare", label: "All comparisons" },
  },
  {
    title: "Research \u00b7 Notes",
    href: "/research",
    rows: [
      { href: "/research/a-workshop-not-a-chatbot", label: "A workshop, not a chatbot", desc: "The argument the whole platform is built on.", icon: Sparkles },
      { href: "/research/the-case-against-one-big-model", label: "Against one big model", desc: "Why a single model is the wrong default.", icon: Bot },
      { href: "/research/owning-your-tools-again", label: "Owning your tools again", desc: "What renting your stack actually costs.", icon: Handshake },
      { href: "/research/tell-you-who-wrote-the-answer", label: "Who wrote the answer", desc: "Naming the model behind every response.", icon: Tags },
    ],
    more: { href: "/research", label: "All notes" },
  },
];

/* Shared by both mega-panels. Kept at module scope so the two menus cannot drift apart — the
   hover treatment, the icon tile inversion and the active state are defined once. */
function PanelRowLink({ r, pathname }: { r: PanelRow; pathname: string }) {
  const isOn = !r.external && (pathname === r.href || pathname.startsWith(`${r.href}/`));
  const Icon = r.icon;
  return (
    <Link
      role="menuitem"
      href={r.href}
      {...(r.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={cn(
        "group flex items-start gap-3 rounded-xl border border-transparent px-3 py-2.5",
        "transition-colors duration-200 hover:border-border hover:bg-foreground/[0.03]",
        "focus-visible:border-border focus-visible:bg-foreground/[0.03] focus:outline-none",
        isOn && "border-border bg-foreground/[0.04]"
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border hairline-strong",
          "transition-colors duration-200 group-hover:bg-foreground group-hover:text-background",
          isOn && "bg-foreground text-background"
        )}
      >
        {r.img ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={r.img} alt="" width={16} height={16} className="h-4 w-4 object-contain" />
        ) : (
          <Icon className="h-4 w-4" aria-hidden />
        )}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-[13px] font-medium leading-tight text-foreground">
          {r.label}
          {r.external && <ArrowUpRight className="h-3 w-3 opacity-50" />}
        </span>
        <span className="mt-1 block text-[11.5px] leading-snug text-foreground/60">{r.desc}</span>
      </span>
    </Link>
  );
}

/* The promoted card every panel ends on. Hoisted for the same reason PanelRowLink was: four
   copies of a canvas-backed hover card is four chances for one of them to fall behind.

   The canvas sits at z-0 inside `isolate`, NOT at -z-10 — a negative index puts it behind the
   card's own background, where it renders perfectly and is never seen. */
function PanelCta({
  href, icon: Icon, title, action, hover, setHover, children,
}: {
  href: string;
  icon: LucideIcon;
  title: string;
  action: string;
  hover: boolean;
  setHover: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      role="menuitem"
      href={href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="group relative isolate flex h-full flex-col justify-between overflow-hidden rounded-xl border hairline bg-background p-4 transition-colors duration-200 hover:border-[hsl(var(--foreground)/0.2)]"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl">
        <CanvasRevealEffect active={hover} maxOpacity={0.3} spacing={12} />
      </div>
      <div className="relative z-10">
        <span className="inline-flex size-8 items-center justify-center rounded-full border hairline-strong transition-colors duration-200 group-hover:bg-foreground group-hover:text-background">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <span className="mt-3 block text-[13px] font-medium leading-tight text-foreground">{title}</span>
        <span className="mt-1.5 block text-[11.5px] leading-snug text-foreground/60">{children}</span>
      </div>
      <span className="relative z-10 mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--accent))]">
        {action}
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  );
}

function PanelChip({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      role="menuitem"
      href={href}
      className="rounded-full border hairline px-2.5 py-1 text-[11px] text-foreground/70 transition-colors hover:border-border hover:bg-foreground/[0.04] hover:text-foreground"
    >
      {children}
    </Link>
  );
}

function WebIndexPanel({ pathname }: { pathname: string }) {
  const [ctaHover, setCtaHover] = useState(false);
  /* A second panel needs its own flag, not a shared one: the canvas wave is driven by a boolean per
     card, so reusing ctaHover would light both whenever either was pointed at. */
  const [submitHover, setSubmitHover] = useState(false);

  return (
    <div className="p-2">
      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,0.82fr)] divide-x divide-[hsl(var(--foreground)/0.06)] rounded-xl border hairline bg-card">
        {WEB_INDEX_COLUMNS.map((col) => (
          <div key={col.title} className="min-w-0 p-2">
            <Link
              href={col.href}
              role="menuitem"
              className="kicker block px-3 pt-1.5 pb-2 transition-colors hover:text-foreground"
            >
              {col.title}
            </Link>
            <div className="flex flex-col gap-0.5">
              {col.rows.map((r) => (
                <PanelRowLink key={r.href} r={r} pathname={pathname} />
              ))}
              {col.more && (
                <Link
                  role="menuitem"
                  href={col.more.href}
                  className="mx-3 mt-1 text-[12px] font-medium text-foreground/60 transition-colors hover:text-foreground hover:underline"
                >
                  {col.more.label} &rarr;
                </Link>
              )}
            </div>
          </div>
        ))}

        {/* ── Partner CTA ────────────────────────────────────────────────────────────────
            Narrower than the link columns so it reads as a panel, not a fifth list.

            The artwork is CanvasRevealEffect — the same dot-matrix wave already used on the
            /platform pillar cards, so this is a pattern the site owns rather than a new one. It
            draws in `hsl(var(--accent))` by default, which is why no colour is passed here: the
            card inherits the same red the rest of askFinz rations, and follows it automatically if
            the token ever changes. It is dependency-free (canvas + RAF, no three.js) and the
            component itself honours prefers-reduced-motion.

            Hover is tracked in state rather than done in CSS because the effect needs a boolean to
            drive the wave, not a style change. ── */}
        {/* Two panels, stacked. "Submit a domain" moved here out of the "Run a node" list, where it
            was the one row that asked nothing of the reader's machine — everything else in that
            column installs software. Sitting among them it read as a fifth install route. Here it
            sits beside the partner panel as what it actually is: the way to contribute without
            running anything. The partner card keeps the height it had (flex-1); this one takes only
            what it needs, so the column's proportions are unchanged. */}
        <div className="min-w-0 p-2 flex flex-col gap-2">
          <div className="min-h-0 flex-1">
            <PanelCta
              href="/partners"
              icon={Handshake}
              title="Become a partner"
              action="How it works"
              hover={ctaHover}
              setHover={setCtaHover}
            >
              Your content read directly from the source and kept fresh with priority, plus access to
              askFinz Indexer OS to run a node of your own.
            </PanelCta>
          </div>
          <div className="shrink-0">
            <PanelCta
              href="/submit-domain"
              icon={Send}
              title="Submit a domain"
              action="Suggest a site"
              hover={submitHover}
              setHover={setSubmitHover}
            >
              Suggest a site worth reading. No account, nothing to install.
            </PanelCta>
          </div>
        </div>
      </div>

      {/* Footer strip — the two generated sets, plus the one live destination. */}
      <div className="mt-2 flex flex-col gap-2 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Link href="/collections" role="menuitem" className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45 hover:text-foreground">
            Collections
          </Link>
          {CORPORA.slice(0, 5).map((c) => (
            <PanelChip key={c.slug} href={`/collections/${c.slug}`}>{c.seoTitle ?? c.title}</PanelChip>
          ))}
          <Link href="/collections" role="menuitem" className="text-[11px] text-foreground/50 hover:text-foreground">
            all {CORPORA.length} &rarr;
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Link href="/web-index" role="menuitem" className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45 hover:text-foreground">
            By industry
          </Link>
          {INDUSTRIES.slice(0, 7).map((i) => (
            <PanelChip key={i.key} href={`/web-index/for/${i.key}`}>{i.label}</PanelChip>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t hairline pt-2.5">
          {/* Live figures, loaded on first open — see nav-live-stats.tsx for why these two and not
              a page total. `active` is the panel's own open state, so nothing is fetched until
              somebody actually looks at the menu. */}
          <NavLiveStats active />
          <div className="flex items-center gap-1">
            {[
              { href: "/captcha", label: "Captcha" },
              { href: "/press", label: "Press" },
            ].map((l) => (
              <Link
                key={l.href}
                href={l.href}
                role="menuitem"
                className="px-2.5 py-1 text-[12px] text-foreground/55 transition-colors hover:text-foreground"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function GuidesPanel({ pathname, data }: { pathname: string; data?: GuidesNavData }) {
  const [ctaHover, setCtaHover] = useState(false);
  const guides = data?.guides ?? [];
  const research = data?.research ?? [];


  return (
    <div className="p-2">
      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,0.82fr)] divide-x divide-[hsl(var(--foreground)/0.06)] rounded-xl border hairline bg-card">
        {GUIDES_COLUMNS.map((col) => (
          <div key={col.title} className="min-w-0 p-2">
            <Link href={col.href} role="menuitem" className="kicker block px-3 pt-1.5 pb-2 transition-colors hover:text-foreground">
              {col.title}
            </Link>
            <div className="flex flex-col gap-0.5">
              {col.rows.map((r) => (
                <PanelRowLink key={r.href} r={r} pathname={pathname} />
              ))}
              {col.more && (
                <Link
                  role="menuitem"
                  href={col.more.href}
                  className="mx-3 mt-1 text-[12px] font-medium text-foreground/60 transition-colors hover:text-foreground hover:underline"
                >
                  {col.more.label} &rarr;
                </Link>
              )}
            </div>
          </div>
        ))}

        <div className="min-w-0 p-2">
          <PanelCta
            href="/beta"
            icon={Sparkles}
            title="Try it yourself"
            action="Request access"
            hover={ctaHover}
            setHover={setCtaHover}
          >
            askFinz is in private access. Join the waitlist, and read the guides while you wait.
          </PanelCta>
        </div>
      </div>

      {/* The generated lists, as chips — the same shape Web Index uses for its generated sets. */}
      <div className="mt-2 flex flex-col gap-2 px-3 py-2.5">
        {guides.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <Link href="/guides" role="menuitem" className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45 hover:text-foreground">
              Latest guides
            </Link>
            {guides.slice(0, 5).map((g) => (
              <PanelChip key={g.href} href={g.href}>{g.label}</PanelChip>
            ))}
          </div>
        )}
        {research.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <Link href="/research" role="menuitem" className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45 hover:text-foreground">
              Notes
            </Link>
            {research.slice(0, 5).map((r) => (
              <PanelChip key={r.href} href={r.href}>{r.label}</PanelChip>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────── Mobile accordion item ──────────────── */

/* A row in the mobile menu. Same anatomy as the desktop PanelRowLink — tile, name, one line of
   description — at touch-target sizing rather than pointer sizing. */
function MobileRow({
  href, label, blurb, icon: Icon, img, external, glyph, badge, onNavigate, active,
}: {
  href: string;
  label: string;
  blurb?: string;
  icon?: LucideIcon;
  img?: string;
  external?: boolean;
  glyph?: string;
  badge?: React.ReactNode;
  onNavigate: () => void;
  active?: boolean;
}) {
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg border hairline-strong",
          glyph && "font-mono text-[11px] leading-none tracking-tight text-foreground/60",
          active && "bg-foreground text-background"
        )}
      >
        {glyph ? (
          <>
            {glyph.charAt(0).toUpperCase()}
            <span className="opacity-75">{glyph.charAt(1).toLowerCase()}</span>
          </>
        ) : img ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={img} alt="" width={16} height={16} className="h-4 w-4 object-contain" />
        ) : Icon ? (
          <Icon className="h-4 w-4" aria-hidden />
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[14px] font-medium leading-tight text-foreground">{label}</span>
          {badge}
          {external && <ArrowUpRight className="h-3 w-3 shrink-0 opacity-50" aria-hidden />}
        </span>
        {blurb && (
          <span className="mt-0.5 block text-[12px] leading-snug text-foreground/60">{blurb}</span>
        )}
      </span>
    </>
  );
  const cls = cn(
    "flex items-center gap-3 rounded-xl border border-transparent px-2 py-2",
    "active:bg-foreground/[0.05] transition-colors",
    active && "border-border bg-foreground/[0.04]"
  );
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onNavigate} className={cls}>
      {body}
    </a>
  ) : (
    <Link href={href} onClick={onNavigate} className={cls}>
      {body}
    </Link>
  );
}

/* A non-expanding row in the mobile menu — Pricing, Company, Sign in. Deliberately the same
   height, type and divider as MobileAccordion's trigger: these sit in one list with the expanding
   entries, and the only thing that should distinguish them is the absence of a chevron. */
function MobileLinkRow({
  href, label, active, external, onNavigate,
}: {
  href: string;
  label: string;
  active?: boolean;
  external?: boolean;
  onNavigate: () => void;
}) {
  const cls = cn(
    "flex items-center justify-between border-b hairline py-2.5 text-[15px] font-medium last:border-b-0",
    active ? "text-foreground" : "text-foreground/80"
  );
  const body = (
    <>
      <span>{label}</span>
      {external && <ArrowUpRight className="h-4 w-4 text-foreground/40" aria-hidden />}
    </>
  );
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onNavigate} className={cls}>
      {body}
    </a>
  ) : (
    <Link href={href} onClick={onNavigate} className={cls}>
      {body}
    </Link>
  );
}

function MobileAccordion({
  item,
  pathname,
  onNavigate,
  guidesData,
}: {
  item: DropdownItem;
  pathname: string;
  onNavigate: () => void;
  guidesData?: GuidesNavData;
}) {
  const active = isActive(pathname, item.match);
  const [open, setOpen] = useState(active);

  /* Every menu resolves to the SAME shape, so the phone renders the same groups, the same
     descriptions and the same icons the desktop panel does. */
  type MGroup = { title: string; href?: string; rows: PanelRow[] };
  let groups: MGroup[] = [];
  let chips: { label: string; href: string; items: { href: string; label: string }[] }[] = [];

  /* THE PROMOTED CARDS THE PHONE WAS DROPPING ENTIRELY.
     Every desktop panel ends on one or two PanelCta cards: "Help us scale", "Become a partner",
     "Submit a domain". The mobile accordion rendered `groups` and `chips` and nothing else, so on a
     phone those destinations did not exist in the menu at all, and /scale in particular was
     reachable only by typing the URL. They are collected here as plain data rather than by reusing
     PanelCta, which is a tall card carrying a canvas animation sized for a desktop column. */
  type MCta = { href: string; icon: LucideIcon; title: string; body: string; action: string };
  let ctas: MCta[] = [];

  if (item.render === "workspaces") {
    /* Restated from WorkspacesPanel, whose card is hardcoded in the desktop component. The counts
       are derived the same way there, so the sentence cannot go stale in one place and not the
       other. */
    const live = APPS.filter((a) => a.status !== "soon").length;
    ctas = [{
      href: "/apps", icon: Layers, title: "One login, every room",
      body: `${live} workspaces are live today and ${APPS.length - live} are on the roadmap, all sharing one sign-in, one memory and one model layer.`,
      action: `Compare all ${APPS.length}`,
    }];
  }

  if (item.render === "webindex") {
    groups = WEB_INDEX_COLUMNS.map((c) => ({ title: c.title, href: c.href, rows: c.rows }));
    chips = [
      { label: "Collections", href: "/collections", items: CORPORA.slice(0, 8).map((c) => ({ href: `/collections/${c.slug}`, label: c.eyebrow })) },
      { label: "By industry", href: "/web-index", items: INDUSTRIES.slice(0, 8).map((i) => ({ href: `/web-index/for/${i.key}`, label: i.label })) },
    ];
    /* Kept in step with WebIndexPanel by hand: these two live inside that bespoke desktop panel
       rather than on the nav item, so there is no shared source to read them from. */
    ctas = [
      { href: "/partners", icon: Handshake, title: "Become a partner",
        body: "Your content read directly from the source and kept fresh with priority, plus access to askFinz Indexer OS to run a node of your own.",
        action: "How it works" },
      { href: "/submit-domain", icon: Send, title: "Submit a domain",
        body: "Suggest a site worth reading. No account, nothing to install.",
        action: "Suggest a site" },
    ];
  } else if (item.render === "guides") {
    groups = GUIDES_COLUMNS.map((c) => ({ title: c.title, href: c.href, rows: c.rows }));
    const g = guidesData?.guides ?? [];
    const r = guidesData?.research ?? [];
    if (g.length) chips.push({ label: "Latest guides", href: "/guides", items: g.slice(0, 6) });
    if (r.length) chips.push({ label: "Notes", href: "/research", items: r.slice(0, 6) });
    /* Restated from GuidesPanel for the same reason. */
    ctas = [{
      href: "/beta", icon: Sparkles, title: "Try it yourself",
      body: "askFinz is in private access. Join the waitlist, and read the guides while you wait.",
      action: "Request access",
    }];
  } else if (item.render !== "workspaces") {
    /* Grouped panels (Platform, Solutions) already carry `group` and `lucide` on each item. */
    for (const it of item.items ?? []) {
      const title = it.group ?? "";
      const row: PanelRow = {
        href: it.href, label: it.label, desc: it.blurb ?? "",
        icon: it.lucide ?? ArrowUpRight, external: it.external, img: it.icon,
      };
      const found = groups.find((x) => x.title === title);
      if (found) found.rows.push(row);
      else groups.push({ title, rows: [row] });
    }
    if (item.chips) chips = [item.chips];
  }

  /* Any dropdown that carries its own card gets it, whatever its render type. Kept outside the
     chain above so a new render mode cannot silently lose the card again. */
  if (!ctas.length && item.cta) ctas = [item.cta];
  /* The desktop band (Model Fab's Data Share) becomes one more card on a phone. */
  if (item.band) ctas = [...ctas, { href: item.band.href, icon: item.band.icon, title: item.band.title, body: item.band.body, action: item.band.action }];

  return (
    <div className={cn("border-b hairline last:border-b-0", open && "pb-2")}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center justify-between py-2.5 text-left text-[15px] font-medium",
          active ? "text-foreground" : "text-foreground/80"
        )}
      >
        <span>{item.label}</span>
        <ChevronDown
          aria-hidden
          className={cn("h-4 w-4 text-foreground/60 transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {/* Rendered only when open. A max-height transition cannot animate a panel whose height is
          unknown, and these range from ~200px to well over 1,000px for Workspaces. */}
      {open && (
        <div className="flex flex-col gap-3 pb-3">
          {item.render === "workspaces"
            ? APP_GROUPS.map((g) => {
                const apps = APPS.filter((a) => a.category === g.id);
                return (
                  <div key={g.id}>
                    <div className="kicker px-2 pb-1.5">{g.label}</div>
                    <div className="flex flex-col gap-0.5">
                      {apps.map((a) => (
                        <MobileRow
                          key={a.slug}
                          href={`/apps/${a.slug}`}
                          label={a.name}
                          blurb={a.tagline}
                          glyph={(a.accent ?? a.name).slice(0, 2)}
                          active={pathname === `/apps/${a.slug}`}
                          badge={a.status === "soon" ? <StatusBadge status={a.status} size="compact" className="ml-auto shrink-0" /> : undefined}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            : groups.map((g) => (
                <div key={g.title || "_"}>
                  {g.title && (
                    g.href ? (
                      <Link href={g.href} onClick={onNavigate} className="kicker block px-2 pb-1.5">
                        {g.title}
                      </Link>
                    ) : (
                      <div className="kicker px-2 pb-1.5">{g.title}</div>
                    )
                  )}
                  <div className="flex flex-col gap-0.5">
                    {g.rows.map((r) => (
                      <MobileRow
                        key={r.href}
                        href={r.href}
                        label={r.label}
                        blurb={r.desc}
                        icon={r.icon}
                        img={r.img}
                        external={r.external}
                        active={!r.external && pathname === r.href}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                </div>
              ))}

          {chips.map((c) => (
            <div key={c.label} className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-2 pt-1">
              <Link href={c.href} onClick={onNavigate} className="shrink-0 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/45">
                {c.label}
              </Link>
              {c.items.map((x) => (
                <Link
                  key={x.href}
                  href={x.href}
                  onClick={onNavigate}
                  className="rounded-full border hairline px-2.5 py-1 text-[11px] text-foreground/70"
                >
                  {x.label}
                </Link>
              ))}
            </div>
          ))}

          {/* The promoted cards, laid out as rows rather than as the desktop's tall tiles: on a
              phone the accordion is already a vertical list, and a full-height card with artwork
              would push everything below it off the screen. */}
          {ctas.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.href}
                href={c.href}
                onClick={onNavigate}
                className="mx-2 mt-1 flex items-start gap-3 rounded-xl border hairline-strong bg-card p-3.5"
              >
                <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border hairline-strong">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="text-[13px] font-medium leading-tight text-foreground">{c.title}</span>
                  <span className="mt-1 text-[11.5px] leading-snug text-foreground/60">{c.body}</span>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--accent))]">
                    {c.action}
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export type PressNavItem = {
  href: string;
  label: string;
  blurb?: string;
  date?: string;
  category?: string | null;
};

export function SiteNav({
  guidesData,
  pressData,
}: {
  guidesData?: GuidesNavData;
  pressData?: PressNavItem[];
}) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = usePathname() ?? "/";

  /* MERGED ONCE, HERE, rather than threaded through four layers as another prop. Both the desktop
     panel and the phone accordion read the same NAV item, so injecting the releases into it is the
     only change either renderer needs. `groupHref` on the first row is what turns the section
     heading into a link to /press — which is why the index page is no longer also listed as a row
     underneath itself. */
  const nav = useMemo<NavItem[]>(() => {
    const press = pressData ?? [];
    return NAV.map((n) => {
      if (n.kind !== "dropdown" || n.label !== "Company") return n;
      return {
        ...n,
        items: [
          ...(n.items ?? []),
          ...press.map((r) => ({
            href: r.href,
            label: r.label,
            blurb: r.blurb,
            group: "Press & media",
            groupHref: "/press",
            lucide: Newspaper,
          })),
          ...COLLAB_ROWS,
        ],
      };
    });
  }, [pressData]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        /* overflow-x-clip: a CLOSED dropdown keeps its last position and can sit past the right
           edge, which made every page scrollable sideways (keyboard focus or scrollIntoView would
           shift the whole layout). Clipping only the x axis keeps dropdowns opening downwards. */
        "sticky top-0 z-50 overflow-x-clip transition-[backdrop-filter,background,border] duration-300",
        // backdrop-blur is expensive on mobile compositors; cap at md+
        // and use a higher background opacity on mobile so the header
        // still reads against scrolling content without the blur.
        scrolled
          ? "border-b hairline bg-background/90 md:bg-background/70 md:backdrop-blur-xl"
          : "border-b border-transparent"
      )}
    >
      <div className="container-x flex h-16 items-center justify-between">
        <Link href="/" aria-label="askFinz home" className="group flex shrink-0 items-center">
          {/*
            Width/height are required by next/image and prevent CLS.
            Source SVG is 1300x256 ⇒ aspect 5.078; at h-6 (24px) ⇒ w=122.
            Switching from <img> to next/image stops React 19 from auto-
            emitting a duplicate <link rel="preload"> for each variant.
            Both kept in DOM (display-toggled by theme) — small SVGs (~16 KB)
            avoid a flash-of-wrong-logo on hydration.
          */}
          <Image
            src="/shared/logos/header/askFinz_black.svg"
            alt="askFinz"
            width={122}
            height={24}
            priority
            className="h-6 w-auto dark:hidden"
          />
          <Image
            src="/shared/logos/header/askFinz_white.svg"
            alt="askFinz"
            width={122}
            height={24}
            loading="lazy"
            className="h-6 w-auto hidden dark:block"
          />
        </Link>

        {/* DESKTOP NAV BREAKPOINT: xl (1280px), not lg.
            At its natural width the header needs ~1,066px (logo 122 + seven items 491 + gaps +
            the right cluster 261, measured 2026-10-07). `container-x` leaves only ~942px of that
            at a 1024px window, so from lg up to ~1150px every item was squeezed: the logo shrank,
            "Web Index", "Sign in" and "Request access" wrapped onto two lines. At 1280 there is
            1,184px, comfortably enough. (It was `md:` before lg, for the same reason one step down.)
            Every show/hide switch below moves together; changing one alone leaves a band with both
            navs visible or neither. */}
        <nav aria-label="Primary" className="hidden xl:flex items-center gap-6 2xl:gap-7 text-sm self-stretch">
          {nav.map((n) => {
            if (n.kind === "link") {
              const active = pathname === n.href || pathname.startsWith(`${n.href}/`);
              return (
                <Link
                  key={n.href}
                  href={n.href}
                  className={cn(
                    "nav-link whitespace-nowrap text-foreground/70 hover:text-foreground",
                    active && "text-foreground"
                  )}
                >
                  {n.label}
                </Link>
              );
            }
            return <DesktopDropdown key={n.label} item={n} pathname={pathname} guidesData={guidesData} />;
          })}
        </nav>

        <div className="hidden xl:flex shrink-0 items-center gap-2 whitespace-nowrap">
          <ThemeToggle />
          <Link
            href={SITE.login}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-foreground/70 hover:text-foreground px-3 py-1.5 rounded-full"
          >
            Sign in
          </Link>
          <Link
            href="/beta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.("event", "request_access_click", { location: "nav" })}
            className="group inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-4 py-1.5 text-sm font-medium hover:opacity-90 transition"
          >
            Request access
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        {/* Below xl the menu collapses, but on a tablet or laptop there is room for the primary
            action, so it stays beside the menu button instead of hiding inside the drawer. */}
        <div className="xl:hidden flex shrink-0 items-center gap-2">
          <Link
            href="/beta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.("event", "request_access_click", { location: "nav_compact" })}
            className="group hidden md:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground text-background px-4 py-1.5 text-sm font-medium hover:opacity-90 transition"
          >
            Request access
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border hairline-strong"
          >
            {open ? <X className="h-4 w-4" aria-hidden /> : <Menu className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-nav" className="xl:hidden border-t hairline bg-background">
          <nav aria-label="Mobile" className="container-x py-4 flex flex-col text-base max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
            {nav.map((n) =>
              n.kind === "link" ? (
                <MobileLinkRow
                  key={n.href}
                  href={n.href}
                  label={n.label}
                  active={pathname === n.href || pathname.startsWith(`${n.href}/`)}
                  onNavigate={() => setOpen(false)}
                />
              ) : (
                <MobileAccordion
                  key={n.label}
                  item={n}
                  pathname={pathname}
                  onNavigate={() => setOpen(false)}
                  guidesData={guidesData}
                />
              )
            )}
            <MobileLinkRow
              href={SITE.login}
              label="Sign in"
              external
              onNavigate={() => setOpen(false)}
            />
            <Link
              href="/beta"
              onClick={() => { (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.("event", "request_access_click", { location: "mobile_nav" }); setOpen(false); }}
              target="_blank"
              rel="noopener noreferrer"
              /* Full width, not `w-fit`. Under a column of full-width rows a small pill reads as an
                 orphan rather than as the primary action. */
              className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground px-4 py-3 text-[15px] font-medium text-background"
            >
              Request access <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
